import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { api, fileUrl } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import Media, { isVideo } from "../components/Media";
import LikeButton from "../components/LikeButton";
import { Heart, Circle, MapPin } from "lucide-react";

const TIER_LABEL = { premium: "Premium", gold: "Gold", standard: "Standard" };

const ClientDashboard = () => {
  const { user } = useAuth();
  const [favs, setFavs] = useState([]);
  const [feed, setFeed] = useState([]);
  const [likeMap, setLikeMap] = useState({});

  const load = async () => {
    const f = await api.get("/me/favorites");
    setFavs(f.data);
    const ff = await api.get("/me/feed");
    setFeed(ff.data);
    const targets = (ff.data || []).map((p) => p.image_path).filter(Boolean);
    if (targets.length) {
      try {
        const lr = await api.post("/likes/batch", { targets });
        setLikeMap(lr.data || {});
      } catch (e) {}
    }
  };
  useEffect(() => { load(); }, []);

  const removeFav = async (a) => {
    await api.delete(`/me/favorites/${a.angel_id}`);
    toast.success("Removida de favoritas");
    load();
  };

  const becomeModel = async () => {
    await api.post("/auth/become-model");
    toast.success("¡Bienvenida! Tu perfil de modelo fue creado.");
    window.location.href = "/dashboard/model";
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#6B6A63]">Tu espacio privado</div>
            <h1 className="font-serif text-4xl text-[#1A2118]">Hola, {user?.name?.split(" ")[0]}</h1>
          </div>
          {user?.role === "client" && (
            <button data-testid="become-model-btn" onClick={becomeModel} className="btn-ghost text-sm">
              ¿Eres modelo? Crear perfil
            </button>
          )}
        </div>

        <Tabs defaultValue="feed">
          <TabsList className="bg-[#F3F1EA]">
            <TabsTrigger data-testid="tab-feed" value="feed">Muro</TabsTrigger>
            <TabsTrigger data-testid="tab-favs" value="favs">Favoritas ({favs.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="feed" className="pt-6 space-y-4">
            {feed.map((p) => (
              <article key={p.post_id} className="border border-[#E5E2D9] rounded-xl bg-white p-4">
                <Link to={`/angel/${p.angel_id}`} className="flex items-center gap-3 hover:opacity-90">
                  {p.main_photo_path && (
                    <img src={fileUrl(p.main_photo_path)} className="w-10 h-10 rounded-full object-cover no-save"
                         alt="" draggable={false} onContextMenu={(e) => e.preventDefault()} />
                  )}
                  <div className="font-serif text-lg text-[#1A2118]">{p.artistic_name}</div>
                  <div className="text-xs text-[#6B6A63] ml-auto">{new Date(p.created_at).toLocaleString("es-CL")}</div>
                </Link>
                {p.text && <div className="mt-2 whitespace-pre-wrap text-[#1A2118]">{p.text}</div>}
                {p.image_path && (
                  <div className="mt-3 relative rounded-lg overflow-hidden bg-[#F3F1EA]">
                    {isVideo(p.image_path) ? (
                      <video src={fileUrl(p.image_path)} className="w-full max-h-[28rem] object-contain no-save"
                             controls muted playsInline loop controlsList="nodownload" disablePictureInPicture
                             onContextMenu={(e) => e.preventDefault()} />
                    ) : (
                      <img src={fileUrl(p.image_path)} className="w-full max-h-[28rem] object-cover no-save" alt=""
                           draggable={false} onContextMenu={(e) => e.preventDefault()} />
                    )}
                    <div className="absolute bottom-2 right-2">
                      <LikeButton target={p.image_path} initial={likeMap[p.image_path]} />
                    </div>
                  </div>
                )}
              </article>
            ))}
            {!feed.length && (
              <div className="text-sm text-[#6B6A63] text-center py-12">
                Aún no hay publicaciones. Marca como favoritas a tus modelos preferidas y aquí verás sus últimas publicaciones.
              </div>
            )}
          </TabsContent>

          <TabsContent value="favs" className="pt-6 space-y-3">
            {favs.map((a) => (
              <div key={a.angel_id} className="flex items-center gap-3 border border-[#E5E2D9] rounded-xl bg-white p-3"
                   data-testid={`fav-row-${a.angel_id}`}>
                <Link to={a.is_published ? `/angel/${a.angel_id}` : "#"}
                      onClick={(e) => !a.is_published && e.preventDefault()}
                      className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="relative w-14 h-20 rounded-lg overflow-hidden bg-[#F3F1EA] shrink-0">
                    {a.main_photo_path ? (
                      <img src={fileUrl(a.main_photo_path)} className="w-full h-full object-cover no-save" alt=""
                           draggable={false} onContextMenu={(e) => e.preventDefault()} />
                    ) : null}
                    {!a.is_published && <div className="absolute inset-0 bg-black/45" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-serif text-lg truncate text-[#1A2118]">{a.artistic_name}</div>
                      <span className="text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-[#F3F1EA] text-[#6B6A63]">{TIER_LABEL[a.tier] || a.tier}</span>
                    </div>
                    <div className="text-xs text-[#6B6A63] flex items-center gap-1 truncate">
                      <MapPin size={12} /> {a.comuna || "—"}
                    </div>
                    <div className="mt-1.5 inline-flex items-center gap-1.5 text-xs">
                      {a.is_published ? (
                        a.is_available ? (
                          <><span className="w-2 h-2 rounded-full available-dot" /> <span className="text-[#22C55E] font-medium">En línea</span></>
                        ) : (
                          <><Circle size={10} className="text-[#364531]" /> <span className="text-[#364531]">Activa</span></>
                        )
                      ) : (
                        <><Circle size={10} className="text-[#A8A9AD]" /> <span className="text-[#6B6A63]">Inactiva — pago vencido</span></>
                      )}
                    </div>
                  </div>
                </Link>
                <button data-testid={`unfav-${a.angel_id}`} onClick={() => removeFav(a)}
                        className="shrink-0 h-10 w-10 rounded-full hover:bg-[#F3F1EA] flex items-center justify-center"
                        aria-label="Quitar de favoritas">
                  <Heart size={20} strokeWidth={1.5} className="fill-[#D4AF37] text-[#D4AF37]" />
                </button>
              </div>
            ))}
            {!favs.length && (
              <div className="text-sm text-[#6B6A63] text-center py-12">Aún no tienes favoritas.</div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ClientDashboard;
